// src/pages/DebtStudio/lib/computeCapI.js

import dayjs from "lib/dayjs";
import { buildDebtServiceSchedule } from "pages/DebtStudio/lib/buildDebtServiceSchedule";

/** Dollars → cents rounding (canonical-ish for DS money outputs) */
function roundCents(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function isCapiApplicableDealType(dealType) {
  const t = String(dealType || "").toLowerCase();
  return t === "new_money" || t === "combined" || t === "combination";
}

function safeIso(d) {
  const x = d ? dayjs(d) : null;
  return x && x.isValid() ? x.format("YYYY-MM-DD") : "";
}

/** UI is percent, treat as percent. Blank/invalid/<=0 => 0. */
function ratePctToDecimal(x) {
  const n = Number(x);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n / 100;
}

/**
 * Earnings day-count basis:
 * - If deal dayCount starts with "ACT/360" => base 360
 * - Else default 365
 *
 * This is intentionally simple. If you want CapI earnings to always be ACT/365,
 * regardless of bond day count, we can hardcode 365.
 */
function earningsYearBasisFromFormDayCount(dayCount) {
  const dc = String(dayCount || "").toUpperCase();
  if (dc.includes("ACT/360")) return 360;
  return 365;
}

function yearFracACT(d0, d1, basis) {
  const a = dayjs(d0);
  const b = dayjs(d1);
  if (!a.isValid() || !b.isValid()) return 0;
  const days = b.diff(a, "day");
  if (!Number.isFinite(days) || days <= 0) return 0;
  return days / (basis || 365);
}

function emptyCapIReturn({ enabled, applicable, cutoffDate = "" } = {}) {
  return {
    enabled: !!enabled,
    applicable: !!applicable,
    capiUse: 0,
    capiInterestThroughDate: 0,
    capiEarningsAssumed: 0,
    cutoffDate,
    includedPaymentDates: [],
    flowsIncluded: [],
    debtServiceAdjustments: [],
  };
}

/**
 * Convert included CapI eligible interest flows to DS adjustment rows.
 *
 * These adjustments offset the covered interest payments in the debt service
 * schedule. The adjustment amount is based on covered eligible interest, not the
 * CapI deposit amount, because investment earnings may reduce the required
 * deposit while the fund still pays the full included eligible interest.
 */
function buildCapiDebtServiceAdjustments(flowsIncluded = []) {
  return (flowsIncluded || [])
    .map((r) => {
      const date = safeIso(r?.date) || String(r?.date || "");
      const interest = roundCents(Number(r?.interest || 0));

      if (!date || interest <= 0) return null;

      return {
        date,
        amount: roundCents(-interest),
        label: "Capitalized Interest",
      };
    })
    .filter(Boolean);
}

/**
 * Simulate CapI fund balance with:
 * - single initial deposit at startDateISO (delivery)
 * - fixed annual simple interest rate, credited ON each payment date
 * - interest payment outflow on that same payment date (after crediting earnings)
 *
 * Returns: { endBalance, minBalance, totalEarnings }
 */
function simulateCapiBalance({
  initialDeposit,
  startDateISO,
  flows, // [{ date, interest }]
  annualRateDec, // decimal, e.g. 0.0325
  yearBasis,
} = {}) {
  let bal = Number(initialDeposit || 0);
  if (!Number.isFinite(bal) || bal < 0) bal = 0;

  const r = Number(annualRateDec || 0);
  const basis = Number(yearBasis || 365);

  const start = dayjs(startDateISO);
  let last = start.isValid() ? start : dayjs(flows?.[0]?.date);

  let minBalance = bal;
  let totalEarnings = 0;

  for (const f of flows || []) {
    const payDate = dayjs(f?.date);
    if (!payDate.isValid()) continue;

    // 1) Credit earnings ON the payment date (based on prior balance and elapsed time)
    const yf = yearFracACT(last, payDate, basis);
    if (yf > 0 && r > 0 && bal > 0) {
      const earned = bal * r * yf; // simple interest
      totalEarnings += earned;
      bal += earned;
    }

    // 2) Pay eligible coupon interest ON the payment date
    const payInt = Number(f?.interest || 0);
    if (Number.isFinite(payInt) && payInt > 0) {
      bal -= payInt;
    }

    if (bal < minBalance) minBalance = bal;
    last = payDate;
  }

  return {
    endBalance: bal,
    minBalance,
    totalEarnings,
  };
}

/**
 * Compute Capitalized Interest (CapI) requirement using canonical interest engine:
 * buildDebtServiceSchedule({ rows, form }).
 *
 * Behavior:
 * - CapI eligible interest requirement = sum of coupon-period interest payments
 *   with payment date <= capiDate (inclusive), net of any prior negative debt
 *   service adjustments already applied to the same payment date, such as
 *   accrued interest received.
 * - If capiInvestmentRate > 0 AND a valid form.deliveryDate exists,
 *   compute minimum initial CapI deposit such that the CapI fund
 *   (earning fixed rate, credited on each payment date) can pay
 *   all included eligible interest payments without going negative.
 *
 * Returns stable shape:
 * {
 *   enabled, applicable,
 *   capiUse,
 *   capiInterestThroughDate,
 *   capiEarningsAssumed,
 *   cutoffDate,
 *   includedPaymentDates,
 *   flowsIncluded,
 *   debtServiceAdjustments,
 *   meta: {...}
 * }
 */
export function computeCapI({
  dealType,
  capiEnabled,
  capiDate,
  capiInvestmentRate,
  rows = [],
  form = {},
  prebuiltDebtServiceSchedule = null,
} = {}) {
  const applicable = isCapiApplicableDealType(dealType);
  const enabled = !!capiEnabled && applicable;

  const cutoffISO = safeIso(capiDate);

  // Always return a stable shape
  if (!enabled) {
    return emptyCapIReturn({
      enabled: false,
      applicable,
    });
  }

  // If enabled but cutoff missing/invalid, treat as 0 here.
  if (!cutoffISO) {
    return emptyCapIReturn({
      enabled: true,
      applicable,
      cutoffDate: "",
    });
  }

  // Build coupon-period DS schedule, unless caller already built it.
  // Important: do NOT pass CapI adjustments here. This lets the schedule include
  // prior adjustments such as accrued interest while avoiding recursive CapI.
  let ds = [];

  if (Array.isArray(prebuiltDebtServiceSchedule)) {
    ds = prebuiltDebtServiceSchedule;
  } else {
    try {
      ds = buildDebtServiceSchedule({ rows, form });
    } catch (e) {
      console.warn("[computeCapI] buildDebtServiceSchedule failed:", e);
      ds = [];
    }
  }

  const cutoff = dayjs(cutoffISO);

  const flowsIncluded = (ds || [])
    .filter((r) => {
      const d = dayjs(r?.date);
      return (
        d.isValid() &&
        (d.isBefore(cutoff, "day") || d.isSame(cutoff, "day"))
      );
    })
    .map((r) => {
      const grossInterest = roundCents(Number(r?.interest || 0));
      const priorDebtServiceAdjustment = roundCents(
        Number(r?.debtServiceAdjustment ?? r?.adjustment ?? 0)
      );

      // Cap-I should cover only the issuer-paid interest burden remaining
      // after prior negative adjustments, especially accrued interest received.
      //
      // Example:
      //   gross first interest       2,500,000
      //   accrued interest adj        -611,111
      //   Cap-I eligible interest    1,888,889
      //
      // Positive prior adjustments do not increase Cap-I eligibility.
      const capiEligibleInterest = roundCents(
        Math.max(0, grossInterest + Math.min(0, priorDebtServiceAdjustment))
      );

      return {
        ...r,
        date: safeIso(r?.date) || String(r?.date || ""),
        interest: capiEligibleInterest,
        grossInterest,
        priorDebtServiceAdjustment,
        capiEligibleInterest,
      };
    })
    .filter((r) => !!r.date && Number(r?.interest || 0) > 0)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  const capiInterestRaw = flowsIncluded.reduce(
    (s, r) => s + (Number.isFinite(r?.interest) ? r.interest : 0),
    0
  );

  const capiInterestThroughDate = roundCents(capiInterestRaw);

  const debtServiceAdjustments = buildCapiDebtServiceAdjustments(flowsIncluded);

  // Earnings modeling inputs
  const rateDec = ratePctToDecimal(
    capiInvestmentRate == null || capiInvestmentRate === ""
      ? 0
      : capiInvestmentRate
  );

  const deliveryISO = safeIso(form?.deliveryDate);
  const yearBasis = earningsYearBasisFromFormDayCount(form?.dayCount);

  const canModelEarnings =
    rateDec > 0 &&
    !!deliveryISO &&
    flowsIncluded.length > 0 &&
    capiInterestThroughDate > 0;

  let earningsAssumed = 0;
  let capiUse = capiInterestThroughDate;

  if (canModelEarnings) {
    // Solve minimal initial deposit that keeps balance >= 0 through all included payments.
    // Upper bound = total included eligible interest (depositing that always works).
    let lo = 0;
    let hi = capiInterestThroughDate;
    let best = hi;

    for (let i = 0; i < 45; i++) {
      const mid = (lo + hi) / 2;
      const sim = simulateCapiBalance({
        initialDeposit: mid,
        startDateISO: deliveryISO,
        flows: flowsIncluded,
        annualRateDec: rateDec,
        yearBasis,
      });

      const feasible = sim.minBalance >= -0.0001;

      if (feasible) {
        best = mid;
        hi = mid;
      } else {
        lo = mid;
      }
    }

    capiUse = roundCents(best);
    earningsAssumed = roundCents(capiInterestThroughDate - capiUse);
    if (earningsAssumed < 0) earningsAssumed = 0;
  }

  return {
    enabled: true,
    applicable,
    capiUse: roundCents(capiUse),
    capiInterestThroughDate,
    capiEarningsAssumed: roundCents(earningsAssumed),
    cutoffDate: cutoffISO,
    includedPaymentDates: flowsIncluded.map((r) => String(r.date)),
    flowsIncluded,
    debtServiceAdjustments,
    meta: {
      capiInvestmentRatePct:
        capiInvestmentRate == null || capiInvestmentRate === ""
          ? 0
          : Number(capiInvestmentRate),
      capiInvestmentRateDec: rateDec,
      basis: "buildDebtServiceSchedule",
      cutoffInclusive: true,
      earningsModel: canModelEarnings
        ? "fixed_rate_simple_interest_credit_on_payment_dates_binary_search"
        : "none",
      earningsYearBasis: yearBasis,
      earningsCrediting: "credited_on_each_payment_date_then_outflow",
      depositDate: deliveryISO || "",
      debtServiceAdjustmentBasis:
        "included_interest_payments_net_of_prior_negative_adjustments",
    },
  };
}
